import * as React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { IdentifierInput } from '../components/IdentifierInput';
import { createVerificationFlow, getVerificationFlow, kratosApi } from '@/lib/api/kratos';
import type { VerificationFlow } from '@/lib/api/kratos';
import { cn } from '@/lib/utils/cn';

/**
 * VerificationPage Component
 * 
 * Handles email verification using Ory Kratos flows
 * Supports both sending verification emails and verifying codes
 */
export function VerificationPage() {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [mode, setMode] = React.useState<'send' | 'verify'>('send');
  
  // Fetch or create verification flow
  const { data: flow, isLoading, error, refetch } = useQuery({
    queryKey: ['verificationFlow', flowId],
    queryFn: async (): Promise<VerificationFlow> => {
      if (flowId) {
        try {
          return await getVerificationFlow(flowId);
        } catch (error) {
          console.warn('Failed to get existing flow, creating new one:', error);
          return await createVerificationFlow();
        }
      }
      return await createVerificationFlow();
    },
    retry: 1,
    staleTime: 0,
  });

  // Verification submission mutation
  const verificationMutation = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      if (!flow) throw new Error('No flow available');
      
      const response = await kratosApi.updateVerificationFlow({
        flow: flow.id,
        updateVerificationFlowBody: values as any,
      });
      return response.data;
    },
    onSuccess: (data) => {
      console.log('Verification response:', data);
      
      // Check if verification was successful - match original Nuxt logic
      if ((data as any).state === 'passed_challenge') {
        console.log('Verification successful, redirecting to login');
        setIsSuccess(true);
        
        // Redirect to login after successful verification (like original)
        setTimeout(() => {
          window.location.href = '/auth/login';
        }, 1500);
        return;
      }
      
      // For security (prevent user enumeration), always switch to verify mode
      // when sending verification email, regardless of whether email exists
      if (mode === 'send') {
        console.log('Switching to verify mode (security: prevent user enumeration)');
        setMode('verify');
        // Don't refetch here to avoid showing potential error messages
        return;
      }
      
      // Update flow data for code verification step
      refetch();
    },
    onError: (error) => {
      console.error('Verification error:', error);
      
      // For security (prevent user enumeration), when sending verification email,
      // always switch to verify mode even if there's an error
      if (mode === 'send') {
        console.log('Error during email send, but switching to verify mode for security');
        setMode('verify');
        return;
      }
      
      // Only refetch for code verification errors
      refetch();
    },
  });

  // Extract CSRF token from flow
  const csrfToken = React.useMemo(() => {
    if (!flow) return '';
    const csrfNode = flow.ui.nodes.find(node => 
      node.attributes.name === 'csrf_token'
    );
    return csrfNode?.attributes.value ? String(csrfNode.attributes.value) : '';
  }, [flow]);

  // Determine mode based on flow state
  React.useEffect(() => {
    if (!flow) return;

    // Check if we should be in verify mode
    const hasCodeField = flow.ui.nodes.some(node => 
      node.attributes.name === 'code' && node.attributes.type === 'text'
    );
    const isCodeActive = (flow as any).active === 'code';
    const isSentEmail = (flow as any).state === 'sent_email';
    
    if (hasCodeField || isCodeActive || isSentEmail) {
      console.log('Setting mode to verify based on flow state:', { hasCodeField, isCodeActive, isSentEmail });
      setMode('verify');
    } else {
      setMode('send');
    }
  }, [flow]);

  // Extract errors from flow messages
  React.useEffect(() => {
    if (!flow) return;

    const newErrors: Record<string, string> = {};
    
    // Global flow errors
    if (flow.ui.messages) {
      flow.ui.messages.forEach(message => {
        if (message.type === 'error') {
          newErrors.general = message.text;
        }
      });
    }

    // Field-specific errors
    flow.ui.nodes.forEach(node => {
      if (node.messages && node.attributes.name) {
        node.messages.forEach(message => {
          if (message.type === 'error' && node.attributes.name) {
            newErrors[node.attributes.name] = message.text;
          }
        });
      }
    });

    setErrors(newErrors);
  }, [flow?.id]);

  const handleSendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }

    try {
      await verificationMutation.mutateAsync({
        method: 'code', // Use 'code' method like original
        email: email.trim(),
        csrf_token: csrfToken,
      });
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setErrors({ code: 'Verification code is required' });
      return;
    }

    try {
      await verificationMutation.mutateAsync({
        method: 'code',
        code: code.trim(),
        csrf_token: csrfToken,
      });
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (errors.email) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    if (errors.code) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.code;
        return newErrors;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>Failed to load verification form</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => refetch()} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Email Verified!</CardTitle>
            <CardDescription>Your email has been successfully verified</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Your email address has been verified successfully. Redirecting to sign in...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {mode === 'send' ? 'Verify Your Email' : 'Enter Verification Code'}
          </CardTitle>
          <CardDescription>
            {mode === 'send' 
              ? 'We need to verify your email address to complete your account setup.'
              : `If the email address exists in our system, we've sent a verification code to ${email}. Please enter it below.`
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Global error message */}
          {errors.general && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
              {errors.general}
            </div>
          )}

          {mode === 'send' ? (
            <form onSubmit={handleSendVerification} className="space-y-4">
              <IdentifierInput
                value={email}
                onChange={handleEmailChange}
                error={errors.email}
                disabled={verificationMutation.isPending}
                placeholder="Enter your email address"
              />

              <Button 
                type="submit" 
                className="w-full" 
                disabled={verificationMutation.isPending || !email.trim()}
              >
                {verificationMutation.isPending ? 'Sending...' : 'Send Verification Email'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="Enter verification code"
                  disabled={verificationMutation.isPending}
                  className={cn(
                    errors.code && "border-destructive focus-visible:ring-destructive"
                  )}
                  aria-invalid={!!errors.code}
                />
                {errors.code && (
                  <p className="text-sm text-destructive" role="alert">
                    {errors.code}
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={verificationMutation.isPending || !code.trim()}
              >
                {verificationMutation.isPending ? 'Verifying...' : 'Verify Email'}
              </Button>
            </form>
          )}

          {/* Mode switching and navigation */}
          <div className="text-center space-y-2">
            {mode === 'verify' && (
              <Button 
                variant="outline" 
                onClick={() => {
                  setMode('send');
                  setCode('');
                  setIsSuccess(false);
                }} 
                className="w-full"
              >
                Send New Code
              </Button>
            )}
            
            <div className="text-sm text-muted-foreground">
              <Link 
                to="/auth/login" 
                className="text-primary hover:underline"
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
